import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for attention tracking using MediaPipe Face Landmarker
 * Detects: looking away, yawning, drowsiness
 */
export const useAttentionTracker = (enabled = false, videoElement = null) => {
  const [isDistracted, setIsDistracted] = useState(false);
  const [distractionType, setDistractionType] = useState(null);
  const [attentionScore, setAttentionScore] = useState(1);
  const [isLoaded, setIsLoaded] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  
  const faceLandmarkerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);
  const distractionCountRef = useRef(0);
  const scoreHistoryRef = useRef([]);

  // Load MediaPipe Face Landmarker
  const loadFaceLandmarker = useCallback(async () => {
    if (faceLandmarkerRef.current) return;

    try {
      // Load the vision tasks library
      const { FilesetResolver, FaceLandmarker } = await import(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/vision_bundle.mjs'
      );

      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm'
      );

      const landmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        numFaces: 1,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true
      });

      faceLandmarkerRef.current = landmarker;
      setIsLoaded(true);
      console.log('Face Landmarker loaded successfully');
    } catch (err) {
      console.error('Error loading Face Landmarker:', err);
      // Fallback - create simple mock
      setIsLoaded(false);
    }
  }, []);

  // Calculate Eye Aspect Ratio (EAR) for drowsiness
  const calculateEAR = useCallback((landmarks, eyeIndices) => {
    if (!landmarks || landmarks.length < 478) return 0.3;
    
    // Vertical distances
    const A = Math.sqrt(
      Math.pow(landmarks[eyeIndices[1]].x - landmarks[eyeIndices[5]].x, 2) +
      Math.pow(landmarks[eyeIndices[1]].y - landmarks[eyeIndices[5]].y, 2)
    );
    const B = Math.sqrt(
      Math.pow(landmarks[eyeIndices[2]].x - landmarks[eyeIndices[4]].x, 2) +
      Math.pow(landmarks[eyeIndices[2]].y - landmarks[eyeIndices[4]].y, 2)
    );
    // Horizontal distance
    const C = Math.sqrt(
      Math.pow(landmarks[eyeIndices[0]].x - landmarks[eyeIndices[3]].x, 2) +
      Math.pow(landmarks[eyeIndices[0]].y - landmarks[eyeIndices[3]].y, 2)
    );
    
    return (A + B) / (2.0 * C);
  }, []);

  // Get blendshape score by name
  const getBlendshapeScore = useCallback((blendshapes, name) => {
    if (!blendshapes || !blendshapes[0]?.categories) return 0;
    const category = blendshapes[0].categories.find(c => c.categoryName === name);
    return category ? category.score : 0;
  }, []);

  // Analyze attention from face data
  const analyzeAttention = useCallback((results) => {
    if (!results.faceLandmarks || results.faceLandmarks.length === 0) {
      setFaceDetected(false);
      return { score: 0.5, distracted: false, type: null };
    }

    setFaceDetected(true);
    const landmarks = results.faceLandmarks[0];
    const blendshapes = results.faceBlendshapes;

    // 1. Check for yawning (mouth open)
    const jawOpen = getBlendshapeScore(blendshapes, 'jawOpen');
    const mouthOpen = getBlendshapeScore(blendshapes, 'mouthOpen') || jawOpen;
    const isYawning = mouthOpen > 0.4 || jawOpen > 0.35;

    // 2. Check for eyes closed (drowsiness)
    const leftEyeBlink = getBlendshapeScore(blendshapes, 'eyeBlinkLeft');
    const rightEyeBlink = getBlendshapeScore(blendshapes, 'eyeBlinkRight');
    const avgBlink = (leftEyeBlink + rightEyeBlink) / 2;
    const eyesClosed = avgBlink > 0.5;

    // 3. Check EAR for droopy eyes
    // Left eye indices: 362, 385, 387, 263, 373, 380
    // Right eye indices: 33, 160, 158, 133, 153, 144
    const leftEAR = calculateEAR(landmarks, [362, 385, 387, 263, 373, 380]);
    const rightEAR = calculateEAR(landmarks, [33, 160, 158, 133, 153, 144]);
    const avgEAR = (leftEAR + rightEAR) / 2;
    const drowsy = avgEAR < 0.2;

    // 4. Check gaze direction (looking away)
    const lookLeft = getBlendshapeScore(blendshapes, 'eyeLookOutLeft');
    const lookRight = getBlendshapeScore(blendshapes, 'eyeLookOutRight');
    const lookUp = getBlendshapeScore(blendshapes, 'eyeLookUpLeft') + getBlendshapeScore(blendshapes, 'eyeLookUpRight');
    const lookDown = getBlendshapeScore(blendshapes, 'eyeLookDownLeft') + getBlendshapeScore(blendshapes, 'eyeLookDownRight');
    const lookingAway = lookLeft > 0.3 || lookRight > 0.3 || lookUp > 0.5 || lookDown > 0.6;

    // 5. Check head pose (looking away with head)
    let headTurned = false;
    if (results.facialTransformationMatrixes && results.facialTransformationMatrixes[0]) {
      const matrix = results.facialTransformationMatrixes[0].data;
      // Simple yaw check from rotation matrix
      if (matrix && matrix.length >= 12) {
        const yaw = Math.abs(Math.asin(matrix[8])) * (180 / Math.PI);
        const pitch = Math.abs(Math.asin(matrix[9])) * (180 / Math.PI);
        headTurned = yaw > 25 || pitch > 20;
      }
    }

    // Determine distraction type
    let type = null;
    let distracted = false;

    if (isYawning) {
      type = 'yawning';
      distracted = true;
    } else if (eyesClosed || drowsy) {
      type = 'drowsy';
      distracted = true;
    } else if (lookingAway || headTurned) {
      type = 'looking_away';
      distracted = true;
    }

    // Calculate attention score (0-1)
    let score = 1;
    if (isYawning) score -= 0.4;
    if (eyesClosed) score -= 0.3;
    if (drowsy) score -= 0.2;
    if (lookingAway) score -= 0.3;
    if (headTurned) score -= 0.2;
    score = Math.max(0, Math.min(1, score));

    return { score, distracted, type };
  }, [calculateEAR, getBlendshapeScore]);

  // Process video frame
  const processFrame = useCallback(() => {
    if (!faceLandmarkerRef.current || !videoElement || !enabled) {
      animationFrameRef.current = requestAnimationFrame(processFrame);
      return;
    }

    const video = videoElement;
    if (video.readyState < 2 || video.currentTime === lastVideoTimeRef.current) {
      animationFrameRef.current = requestAnimationFrame(processFrame);
      return;
    }

    lastVideoTimeRef.current = video.currentTime;

    try {
      const results = faceLandmarkerRef.current.detectForVideo(video, performance.now());
      const { score, distracted, type } = analyzeAttention(results);

      // Smooth score with moving average
      scoreHistoryRef.current.push(score);
      if (scoreHistoryRef.current.length > 10) {
        scoreHistoryRef.current.shift();
      }
      const smoothScore = scoreHistoryRef.current.reduce((a, b) => a + b, 0) / scoreHistoryRef.current.length;
      setAttentionScore(smoothScore);

      // Require multiple consecutive detections to trigger
      if (distracted) {
        distractionCountRef.current++;
        if (distractionCountRef.current >= 8) { // ~0.5 seconds at 15fps
          setIsDistracted(true);
          setDistractionType(type);
        }
      } else {
        distractionCountRef.current = Math.max(0, distractionCountRef.current - 2);
        if (distractionCountRef.current === 0) {
          setIsDistracted(false);
          setDistractionType(null);
        }
      }
    } catch (err) {
      console.error('Frame processing error:', err);
    }

    animationFrameRef.current = requestAnimationFrame(processFrame);
  }, [enabled, videoElement, analyzeAttention]);

  // Initialize when enabled
  useEffect(() => {
    if (enabled && !faceLandmarkerRef.current) {
      loadFaceLandmarker();
    }
  }, [enabled, loadFaceLandmarker]);

  // Start processing frames
  useEffect(() => {
    if (enabled && isLoaded && videoElement) {
      processFrame();
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [enabled, isLoaded, videoElement, processFrame]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close();
        faceLandmarkerRef.current = null;
      }
    };
  }, []);

  // Reset distraction state (for after scolding)
  const resetDistraction = useCallback(() => {
    setIsDistracted(false);
    setDistractionType(null);
    distractionCountRef.current = 0;
  }, []);

  return {
    isDistracted,
    distractionType,
    attentionScore,
    isLoaded,
    faceDetected,
    resetDistraction
  };
};

export default useAttentionTracker;
